/**
 * جزر الواجهة المؤجَّلة (optional islands).
 *
 * سبب المشكلة الجذريّ: كلّ `React.lazy(() => import(x).then(m => m.Name))`
 * يقرأ الحقل من مساحة الوحدة مباشرةً. إن وصلت المساحة `undefined`
 * (chunk مقطوع، متصفّح داخل تطبيق، زاحف يوقف الطلب) يرمي:
 * `undefined is not an object (evaluating 'e.WhatsAppFab')`.
 *
 * الحلّ الجذريّ: مصنع واحد لكلّ الجزر غير الحرجة —
 *   يتحقّق من التصدير، يعيد المحاولة مرّة واحدة، ثمّ يهبط لمكوّن فارغ.
 * لا حاجة لحرّاس داخل كلّ مكوّن على حدة.
 */
import * as React from "react";

const NoopIsland: React.ComponentType<Record<string, unknown>> = () => null;

export function lazyIsland<P extends object = Record<string, unknown>>(
  load: () => Promise<unknown>,
  exportName: string,
): React.LazyExoticComponent<React.ComponentType<P>> {
  const resolve = async (): Promise<{ default: React.ComponentType<P> }> => {
    const mod = (await load()) as Record<string, unknown> | null | undefined;
    const found = mod?.[exportName] ?? mod?.["default"];
    if (typeof found !== "function" && typeof found !== "object") {
      throw new Error(`island-export-missing:${exportName}`);
    }
    return { default: found as React.ComponentType<P> };
  };

  return React.lazy(async () => {
    try {
      return await resolve();
    } catch {
      await new Promise((r) => setTimeout(r, 400));
      try {
        return await resolve();
      } catch {
        // جزيرة غير حرجة: نُخفيها بهدوء بدل إسقاط الصفحة أو ضخّ أخطاء وهميّة.
        return { default: NoopIsland as unknown as React.ComponentType<P> };
      }
    }
  });
}

/**
 * نفس الحماية مع استنتاج أنواع الخصائص من الوحدة نفسها.
 * استعملها بدل `React.lazy(() => import(x).then((m) => ({ default: m.Y })))`.
 */
export function island<M, K extends keyof M>(
  load: () => Promise<M>,
  exportName: K,
): M[K] extends React.ComponentType<infer P>
  ? React.LazyExoticComponent<React.ComponentType<P>>
  : never {
  return lazyIsland(
    load as unknown as () => Promise<unknown>,
    exportName as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any;
}

/** زواحف/بوتات: لا تحتاج أيّ جزيرة تفاعليّة، ولا نحمّلها لها. */
export function isCrawlerUA(ua: string | undefined): boolean {
  if (!ua) return false;
  return /bot|crawler|spider|crawling|adsbot|externalagent|headlesschrome|lighthouse|pagespeed|preview|slurp/i.test(
    ua,
  );
}
